import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { TaskTemplateDto } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import {
  useCaseOptions,
  useClientOptions,
  useProjectOptions,
  useTemplates,
  useUsers,
} from '../../hooks/useLookups'
import { DateInput } from '../common/fields'
import { ErrorAlert } from '../common/Feedback'

type Props = {
  open: boolean
  /** Pre-filled associations when opening from a case, client or project card. */
  defaults?: { clientId?: string; caseId?: string; projectId?: string }
  onClose: () => void
  onApplied?: (parentTaskId: string) => void
}

/**
 * Applying a template copies it. The user picks which items to include and who each
 * one goes to; the default is every item, all to the same employee (spec §4.5).
 */
export function ApplyTemplateDialog({ open, defaults, onClose, onApplied }: Props) {
  const queryClient = useQueryClient()
  const templates = useTemplates()
  const users = useUsers()
  const clients = useClientOptions()
  const cases = useCaseOptions()
  const projects = useProjectOptions()

  const [templateId, setTemplateId] = useState('')
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [defaultAssigneeId, setDefaultAssigneeId] = useState('')
  const [perItemAssignee, setPerItemAssignee] = useState<Record<string, string>>({})
  const [parentTaskName, setParentTaskName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [clientId, setClientId] = useState('')
  const [caseId, setCaseId] = useState('')
  const [projectId, setProjectId] = useState('')

  const template = useMemo(
    () => templates.data?.find((t) => t.id === templateId) ?? null,
    [templates.data, templateId]
  )

  useEffect(() => {
    if (!open) return
    setTemplateId('')
    setSelectedItems([])
    setPerItemAssignee({})
    setDefaultAssigneeId('')
    setParentTaskName('')
    setStartDate('')
    setDueDate('')
    setClientId(defaults?.clientId ?? '')
    setCaseId(defaults?.caseId ?? '')
    setProjectId(defaults?.projectId ?? '')
  }, [open, defaults?.clientId, defaults?.caseId, defaults?.projectId])

  const chooseTemplate = (next: TaskTemplateDto | null) => {
    setTemplateId(next?.id ?? '')
    setSelectedItems(next ? next.items.map((item) => item.id) : [])
    setPerItemAssignee({})
    setParentTaskName(next?.name ?? '')
  }

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ parentTaskId: string; createdCount: number }>(`/task-templates/${templateId}/apply`, {
        itemIds: selectedItems,
        defaultAssigneeId,
        perItemAssignee,
        parentTaskName,
        startDate,
        dueDate,
        clientId,
        caseId,
        projectId,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: qk.myDay })
      onApplied?.(result.parentTaskId)
      onClose()
    },
  })

  const toggleItem = (itemId: string) =>
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    )

  const userMenuItems = [
    <MenuItem key="" value="">
      כמו ברירת המחדל
    </MenuItem>,
    ...(users.data ?? []).map((user) => (
      <MenuItem key={user.id} value={user.id}>
        {user.name}
      </MenuItem>
    )),
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>יצירת משימות מתבנית</DialogTitle>

      <DialogContent dividers>
        {apply.error ? <ErrorAlert error={apply.error} /> : null}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={templates.data ?? []}
              value={template}
              loading={templates.isPending}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_event, option) => chooseTemplate(option)}
              noOptionsText="אין תבניות"
              renderInput={(params) => <TextField {...params} label="תבנית" required />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="עובד מטפל (ברירת מחדל לכל הפריטים)"
              value={defaultAssigneeId}
              onChange={(e) => setDefaultAssigneeId(e.target.value)}
            >
              <MenuItem value="">ללא שיוך</MenuItem>
              {(users.data ?? []).map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={12}>
            <TextField
              label="שם משימת האב"
              value={parentTaskName}
              onChange={(e) => setParentTaskName(e.target.value)}
              helperText="ברירת המחדל היא שם התבנית"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <DateInput label="תאריך התחלה" value={startDate} onChange={setStartDate} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DateInput label="תאריך יעד" value={dueDate} onChange={setDueDate} />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="לקוח"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <MenuItem value="">ללא</MenuItem>
              {(clients.data ?? []).map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField select label="תיק" value={caseId} onChange={(e) => setCaseId(e.target.value)}>
              <MenuItem value="">ללא</MenuItem>
              {(cases.data ?? []).map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="פרויקט"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <MenuItem value="">ללא</MenuItem>
              {(projects.data ?? []).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {template ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              פריטים ({selectedItems.length} מתוך {template.items.length})
            </Typography>
            <Paper variant="outlined">
              <Stack divider={<Divider />}>
                {template.items.map((item) => (
                  <Stack
                    key={item.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    sx={{ px: 1.5, py: 1 }}
                  >
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2">{item.name}</Typography>
                      {item.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      ) : null}
                    </Box>
                    <TextField
                      select
                      label="עובד"
                      sx={{ minWidth: 200 }}
                      disabled={!selectedItems.includes(item.id)}
                      value={perItemAssignee[item.id] ?? ''}
                      onChange={(e) =>
                        setPerItemAssignee((prev) => {
                          const next = { ...prev }
                          if (e.target.value) next[item.id] = e.target.value
                          else delete next[item.id]
                          return next
                        })
                      }
                    >
                      {userMenuItems}
                    </TextField>
                  </Stack>
                ))}
              </Stack>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              הפעלת התבנית היא העתקה — עריכת התבנית בהמשך לא תשנה את המשימות שייווצרו כאן.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          onClick={() => apply.mutate()}
          disabled={!templateId || selectedItems.length === 0 || apply.isPending}
        >
          {apply.isPending ? 'יוצר…' : `צור ${selectedItems.length} משימות`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
